function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
import { useRef } from 'react';
import { useAttuned } from "./useAttuned";
import { Scribe } from "../util";

/**
 * Represents a team member respondent with optional additional data based on the specified flags.
 *
 * This type conditionally includes additional data interfaces or types based on the values
 * of the `IncludeMemberDataFlags`. The resulting type is a combination of the base `Member`
 * type and the additional data interfaces if their corresponding flags are enabled.
 *
 * @example
 * // `TeamMemberRespondent` is a generic and accepts an object with boolean flags
 * const r: TeamMemberRespondent<{ include_motivation_status: true }> = { ... }
 * console.log(r.motivation_survey_completion_date) // motivation data will be included.
 *
 * @example
 * // `TeamMemberRespondent` multiple flags can be specified
 * const r: TeamMemberRespondent<{ include_motivation_status: true, include_user_data: true }> = { ... }
 * console.log(r.motivation_survey_completion_date) // motivation data will be included.
 * console.log(r.user_role_id) // user data will be included.
 *
 * @template F - A generic representing the flags that determine which optional data should be included.
 *                Each flag is a key within `IncludeMemberDataFlags<boolean, boolean, boolean, boolean>`.
 *                Possible flags include:
 *                  - `include_user_data`: Determines if `MemberUserData` is included.
 *                  - `include_belonging_teams`: Determines if `MemberTeamData` is included.
 *                  - `include_engagement_stats`: Determines if `MemberEngagementData` is included.
 *                  - `include_motivation_stats`: Determines if `MemberMotivationData` is included.
 *
 * @typeParam Member - Represents the base type for a team member.
 * @typeParam MemberUserData - Represents user-specific data for a member, included when `include_user_data` is true.
 * @typeParam MemberTeamData - Represents data related to teams that the member belongs to, included when `include_belonging_teams` is true.
 * @typeParam MemberEngagementData - Represents engagement statistics for the member, included when `include_engagement_stats` is true.
 * @typeParam MemberMotivationData - Represents motivation statistics for the member, included when `include_motivation_stats` is true.
 */

export function useTeamRespondents(_ref) {
  var accountId = _ref.accountId,
    teamId = _ref.teamId,
    offset = _ref.offset,
    search_term = _ref.search_term,
    statuses = _ref.statuses,
    intersect_team_ids = _ref.intersect_team_ids,
    survey_status_ids = _ref.survey_status_ids,
    sort_by = _ref.sort_by,
    respondent_role_ids = _ref.respondent_role_ids,
    number_of_engagements = _ref.number_of_engagements,
    motivation_completion_dates = _ref.motivation_completion_dates,
    last_engagement_completion_dates = _ref.last_engagement_completion_dates,
    is_favorite = _ref.is_favorite,
    _ref$limit = _ref.limit,
    limit = _ref$limit === void 0 ? 4 : _ref$limit,
    include_user_data = _ref.include_user_data,
    include_belonging_teams = _ref.include_belonging_teams,
    include_engagement_stats = _ref.include_engagement_stats,
    include_motivation_stats = _ref.include_motivation_stats;
  var contentRange = useRef({
    start: 0,
    end: 0,
    total: 0
  });
  var queryParams = {
    offset: offset,
    search_term: search_term,
    statuses: statuses,
    intersect_team_ids: intersect_team_ids,
    survey_status_ids: survey_status_ids,
    sort_by: sort_by,
    respondent_role_ids: respondent_role_ids,
    number_of_engagements: number_of_engagements,
    motivation_completion_dates: motivation_completion_dates,
    last_engagement_completion_dates: last_engagement_completion_dates,
    is_favorite: is_favorite,
    limit: limit,
    include_user_data: include_user_data,
    include_belonging_teams: include_belonging_teams,
    include_engagement_stats: include_engagement_stats,
    include_motivation_stats: include_motivation_stats
  };
  var path = "/v2/accounts/".concat(accountId, "/teams/").concat(teamId, "/members");
  try {
    var _useAttuned = useAttuned(path, {
        search: queryParams,
        exposeHeaders: ['content-range']
      }),
      data = _useAttuned.data,
      isLoading = _useAttuned.isLoading,
      headers = _useAttuned.headers;
    if (!isLoading) {
      var _ref2 = headers['content-range'] ? headers['content-range'].split(' ')[1].split('/') : ['0-0', 0],
        _ref3 = _slicedToArray(_ref2, 2),
        range = _ref3[0],
        total = _ref3[1];
      var _range$split = range.split('-'),
        _range$split2 = _slicedToArray(_range$split, 2),
        start = _range$split2[0],
        end = _range$split2[1];
      contentRange.current.start = Number(start);
      contentRange.current.end = Number(end);
      contentRange.current.total = Number(total);
    }
    return {
      contentRange: contentRange.current,
      data: data !== null && data !== void 0 ? data : [],
      isLoading: isLoading
    };
  } catch (err) {
    Scribe.error('useTeamRespondents :: An error occurred while fetching data', err);
    return {
      contentRange: contentRange.current,
      data: [],
      isLoading: false
    };
  }
}